/**
 * MagicBI Design System - Color Tokens
 *
 * This file contains all color tokens for the design system, organized by
 * semantic meaning and supporting both light and dark themes.
 */

// Raw color values from brand palette
export const rawColors = {
  // Brand colors
  brand: {
    primary: "#6442B3",
    primaryLight: "#AA97DB",
    primaryDark: "#4B3286",
    primaryDarker: "#18102B",
  },

  // Grey scale (updated to match design spec)
  grey: {
    50: "#F7F6FA",
    100: "#F3F1F7",
    200: "#E6E1F3",

    300: "#EEEDF2",
    400: "#DEDCE5",
    500: "#AFADBA",
    600: "#726F80",
    700: "#494754",
    800: "#494754",
    900: "#191236",
  },

  // Structural colors (from design spec)
  structural: {
    white: "#FFFFFF",
    /** Deep ink surface: dark bands, dark raised surfaces (the data app header band). */
    ink: "#241C35",
    /** Figma `--structural-theme-light` — KG builder / light page wash */
    themeLight: "#FBFAFF",
    /** Figma connection-detail field card — `structural/100` at 75% opacity */
    themeLight75: "rgba(244, 240, 255, 0.75)",
    /** Figma 6590:210646 chart point-action "Nav item" fill — unbound in Figma, no grey step matches */
    navItem: "#F5F5F7",
    50: "#F7F6FA",
    // 100: "#F0F6FF",
    // 200: "#E4EBF5",
    100: "#F4F0FF",
    200: "#E9E4F5",
  },

  // Primary scale
  primary: {
    // 100: "#E5EEFF",
    // 200: "#ABC8FF",
    // 300: "#6E9AEC",
    // 400: "#3B73DF",
    // 500: "#1656CF",
    // 600: "#083C9F",
    // 700: "#092863",
    // 800: "#04122A",
    // 900: "#041840",
    // new color
    50: "#F0F6FF",
    100: "#F0ECF7",
    200: "#CFC4E7",
    300: "#9780CC",
    400: "#8368C2",
    500: "#6442B3",
    600: "#5B3CA3",
    /** Soft lavender for hints on dark surfaces (story redesign): tooltip kbd, toast button, extension subtitles */
    lavender: "#AA97D8",
    700: "#472F7F",
    800: "#372462",
    900: "#2A1C4B",
  },

  secondary: {
    100: "#C2F5FF",
    300: "#69E0F6",
    500: "#1CAEC8",
    700: "#13879C",
    900: "#0C525E",
  },
  // Stroke colors
  stroke: {
    100: "#EBF2FE",
    200: "#E6E1F3",
    /** Violet-grey strong divider / input border (story redesign) */
    strong: "#CFCAD9",
    300: "#C6D0E1",
    ///extra colors
    400: "#FB975B",
    500: "#F669E1",
    600: "#3A73DF",
    700: "#69E0F6",
  },

  // Accent colors
  /** Translucent overlays for dark surfaces (hover fills, hairlines, tiles on ink). */
  alpha: {
    white8: "rgba(255, 255, 255, 0.08)",
    white18: "rgba(255, 255, 255, 0.18)",
    /** Translucent ink (brand 900 = #18102B) for floating dark chrome. */
    inverse92: "rgba(24, 16, 43, 0.92)",
  },

  accent: {
    /** Status tones that stay legible on the ink surface. */
    onInk: {
      caution: "#F5C26B",
      success: "#7FD8B0",
      danger: "#F59AA0",
    },
    purpleLight: "#EBDEFF",
    purple: "#5D1BBE",
    pinkLight: "#FFDEED",
    pink: "#FF0A80",
    peachLight: "#FFF3F2",
    peach: "#FB975B",
    greenLight: "#DEFFED",
    green: "#1BBE65",
    yellow500: "#EAB308",
    yellowLight: "#FEFCE8",
    amber500: "#F59E0B",

    amberLight: "rgba(245, 158, 11, 0.10)",
    tableBlue: "#0911FF",
    tableBlueBg: "rgba(9, 17, 255, 0.10)",
    pkColor: "#CA3500",
    pkBg: "#FFEDD4",
    pkBorder: "#FFD6A8",
    fkColor: "#1447E6",
    fkBg: "#DBEAFE",
    fkBorder: "#BEDBFF",
    dataStoryPurple: "#9A13D5",
    dataStoryPurpleBg: "rgba(154, 19, 213, 0.10)",
    dataModelRed: "#FD5A49",
    dataModelRedBg: "rgba(253, 90, 73, 0.10)",
    ontologyGreen: "#139869",
    /** Figma ontology node — 10% tint for icon fill, card shadow, selection halo */
    ontologyGreenBg: "rgba(19, 152, 105, 0.10)",
    metricBlue: "#00B1D1",
    metricBlueBg: "rgba(1, 189, 222, 0.10)",
    artifactBlue: "#219EFF",
    artifactBlueBg: "rgba(120, 191, 245, 0.1)",
    tribalPurple: "#6200CB",
    warningYellow: "#EAB308",
    // Design-system tokens — mirror Figma variables `accent/green/500` and `accent/red/500`.
    // Used by status badges (e.g. connector card connected/error states).
    green500: "#16A34A",
    /** Figma AUTO metric badge background on add-data-model picker */
    greenMetricBg: "#DCFCE7",
    red500: "#DC2626",
    turquoiseLight: "#C2F5FF",
    turquoise: "#1CAEC8",
    /** Drill-through accent (story redesign) */
    drill: {
      bg: "#FFF1E8",
      border: "#FBC9A6",
      solid: "#FB975B",
      text: "#9A4A12",
    },
    /** Cross-filter / teal tint (story redesign); text = secondary 900 */
    cross: {
      bg: "#E8F8FB",
      border: "#69C6D9",
      text: "#0C525E",
    },
    /** Positive tint (story redesign): KPI delta up, LIVE chip */
    positive: {
      bg: "#E3F5EC",
      text: "#17805A",
      solid: "#1F9D6B",
    },
    /** Negative tint (story redesign): KPI delta down, tile error state */
    negative: {
      bg: "#FCE8E9",
      text: "#B03A43",
      textStrong: "#A52A33",
      solid: "#D2414B",
    },
    /** Informational blue (story redesign) */
    infoBlue: {
      bg: "#E7EFFC",
      solid: "#2F6FD6",
    },
    /** Soft danger (washed red): error banners, destructive tint. `solid` is the icon / text-on-white red. */
    danger: {
      soft: {
        bg: "#FCE8E9",
        border: "#F3C4C7",
        text: "#A8303A",
        solid: "#D2414B",
      },
    },
    /** Caution (amber) tint for notices and tags; `solid` is the amber icon / chart series. */
    caution: {
      bg: "#FCF2DF",
      text: "#6B4205",
      solid: "#C27A0B",
    },
    /** Small status tag tones (bg + text); keyed by meaning, not by feature. */
    tag: {
      success: { bg: "#E3F5EC", fg: "#146B49" },
      danger: { bg: "#FCE8E9", fg: "#A8303A" },
      info: { bg: "#E7EFFC", fg: "#1F4E9C" },
      accent: { bg: "#F0ECF7", fg: "#6442B3" },
      neutral: { bg: "#F3F1F7", fg: "#3F3850" },
      neutralStrong: { bg: "#E6E1F3", fg: "#3F3850" },
      warning: { bg: "#FBF1DC", fg: "#6B4205" },
      caution: { bg: "#FCF2DF", fg: "#6B4205" },
    },
    /** "Coming soon" tag (F23): peach tint */
    soon: {
      bg: "#FFF1E8",
      text: "#9A4A12",
    },
  },

  // Text colors (with contrast ratios from design spec)
  text: {
    highEmphasis: "#18102B", // Titles, Important/highlighted text
    strongEmphasis: "#3F3850", // Strong body text, between high and medium
    mediumEmphasis: "#5C5468", // Subtitles, body
    lowEmphasis: "#7A7287", // Labels, tertiary text
    subtleEmphasis: "#A39CB0", // Placeholders, quaternary text
    white: "#FFFFFF", // #000000 - Buttons, left navigation
    interactive: "#6442B3",
  },

  // AI gradients
  ai: {
    stroke:
      "linear-gradient(90deg, #FF69B4 0%, #FFA500 25%, #00CED1 75%, #1E90FF 100%)",
    chatBackground: "rgba(108, 99, 255, 0.1)",
    iconPrimary: "#5D1BBE",
    iconSecondary: "#18102B",
  },

  // Stroke gradients (Figma "New brand color" collection)
  strokeGradient: {
    /** Blue→magenta 1px border, e.g. present-mode slide filmstrip */
    brand: "linear-gradient(90deg, #2665D3 0%, #B328A9 100%)",
  },

  // Background gradients (from design spec)
  backgroundGradient: {
    primary: "linear-gradient(135deg, #EBF3FF 0%, #F4EAF3 100%)",
    secondary: "linear-gradient(135deg, #FFF7F2 0%, #F4EAF3 100%)",
    subtle: "linear-gradient(135deg, #FAFAFC 0%, #FFFFFF 100%)",
    /** Figma "Follow up insights" card (connection detail — Data studio card). */
    insight: "linear-gradient(169deg, #F4F0FF 4.29%, #FFF5F9 96.37%)",
    /** Assistant chat bubble (Figma chat panel spec). */
    chatAssistant:
      "linear-gradient(107deg, #EFF6FF 13.55%, #FFF2FD 47.95%, #FFFAF5 83.27%, #EFF6FF 103.12%)",
    /** Create-story AI method card. The white layer is its structural/0 fallback. */
    storyCreationAi:
      "linear-gradient(108deg, #EFF6FF 9.73%, #FFFAF5 23.81%, #FFF0FD 52.44%, #EBF3FF 81.42%), #FFFFFF",
  },

  // Canvas-specific tokens — KG canvas background and surface colors.
  canvas: {
    /** Dot-grid color painted by ReactFlow <Background variant="dots">. */
    dotGrid: "#E4EAF2",
    /**
     * Dark navy used for canvas control tooltips.
     * Matches the deep blue-navy in the Figma control panel spec.
     * Distinct from text.highEmphasis (#18102B) which reads as near-black violet.
     */
    tooltipDark: "#1B2D4A",
  },
  /** Look-menu accent bar: brand violet to cyan to pink. */
  gradient: {
    lookBar: "linear-gradient(90deg, #6442B3, #22D3EE, #F472B6)",
  },
} as const;

const deepFreeze = <T>(value: T): T => {
  if (value && typeof value === "object") {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

/**
 * Literal hex for code that cannot take a CSS variable: Vega specs, canvas fillStyle,
 * colour maths, exports, and anything persisted. In the running app `rawColors` resolves
 * to `var(--mb-…)` so it follows the light/dark mode; `rawHex` never does.
 */
export const rawHex: typeof rawColors = deepFreeze(
  JSON.parse(JSON.stringify(rawColors)),
);

// Semantic color tokens
export const lightThemeColors = {
  // Primary colors
  primary: {
    50: rawColors.primary[50],
    100: rawColors.primary[100],
    200: rawColors.primary[200],
    300: rawColors.primary[300],
    400: rawColors.primary[400],
    500: rawColors.brand.primary, // Main brand color
    600: rawColors.primary[600],
    700: rawColors.primary[700],
    800: rawColors.brand.primaryDark,
    900: rawColors.brand.primaryDarker,
  },

  // Grey scale
  grey: {
    50: rawColors.text.white,
    100: rawColors.grey[50],
    200: rawColors.grey[100],
    300: rawColors.grey[200],
    400: rawColors.grey[300],
    500: rawColors.grey[400],
    600: rawColors.grey[500],
    700: rawColors.grey[600],
    800: rawColors.grey[700],
    900: rawColors.grey[900],
  },

  // Semantic colors
  background: {
    primary: rawColors.structural.white,
    secondary: rawColors.structural[50],
    tertiary: rawColors.structural[100],
    inverse: rawColors.grey[900],
    inverseHover: rawColors.grey[800],
    gradientPrimary: rawColors.backgroundGradient.primary,
    gradientSecondary: rawColors.backgroundGradient.secondary,
    gradientSubtle: rawColors.backgroundGradient.subtle,
  },

  surface: {
    primary: rawColors.text.white,
    secondary: rawColors.grey[50],
    tertiary: rawColors.grey[100],
    /** Boxed DetailField / connection-info KPI cards (Figma stroke-200 + structural/100 @ 75%). */
    detailField: rawColors.structural.themeLight75,
    raised: rawColors.text.white,
    overlay: "rgba(25, 18, 54, 0.4)",
  },

  border: {
    subtle: rawColors.grey[200],
    default: rawColors.grey[300],
    strong: rawColors.grey[500],
    inverse: rawColors.grey[100],
    stroke: rawColors.stroke[200],
  },

  text: {
    primary: rawColors.text.highEmphasis,
    secondary: rawColors.text.mediumEmphasis,
    tertiary: rawColors.text.lowEmphasis,
    disabled: rawColors.grey[400],
    inverse: rawColors.text.white,
    link: rawColors.brand.primary,
    linkHover: rawColors.brand.primaryDark,
    // Mirrors Figma variable `text/interactive` — used for in-card link/CTA text.
    // Distinct from `text.link` (brand purple) — this is the design-system primary blue.
    interactive: rawColors.primary[500],
  },

  // Interactive states
  interactive: {
    primary: rawColors.brand.primary,
    primaryHover: rawColors.brand.primaryDark,
    primaryActive: rawColors.brand.primaryDarker,
    primaryDisabled: rawColors.grey[400],

    secondary: rawColors.text.white,
    secondaryHover: rawColors.grey[50],
    secondaryActive: rawColors.grey[100],

    tertiary: "transparent",
    tertiaryHover: rawColors.grey[50],
    tertiaryActive: rawColors.grey[100],
  },

  // Status colors
  success: {
    50: rawColors.accent.greenLight,
    500: rawColors.accent.green,
    600: "#159957",
    text: "#0F7B47",
  },

  warning: {
    50: rawColors.accent.peachLight,
    500: rawColors.accent.peach,
    600: "#E8814A",
    text: "#B8693C",
  },

  error: {
    50: rawColors.accent.pinkLight,
    500: rawColors.accent.pink,
    600: "#E6096E",
    text: "#C7085D",
  },

  info: {
    50: rawColors.accent.turquoiseLight,
    500: rawColors.accent.turquoise,
    600: "#189BB3",
    text: "#157A8F",
  },

  // Accent colors
  accent: {
    purple: {
      light: rawColors.accent.purpleLight,
      main: rawColors.accent.purple,
    },
    pink: {
      light: rawColors.accent.pinkLight,
      main: rawColors.accent.pink,
    },
    peach: {
      light: rawColors.accent.peachLight,
      main: rawColors.accent.peach,
    },
    green: {
      light: rawColors.accent.greenLight,
      main: rawColors.accent.green,
    },
    turquoise: {
      light: rawColors.accent.turquoiseLight,
      main: rawColors.accent.turquoise,
    },
  },

  // AI specific colors
  ai: {
    gradient: rawColors.ai.stroke,
    background: rawColors.ai.chatBackground,
    iconPrimary: rawColors.ai.iconPrimary,
    iconSecondary: rawColors.ai.iconSecondary,
  },
} as const;

/**
 * Dark theme keeps the pre-redesign grey values; rawColors.grey[50|100|200]
 * moved to the violet set for the light theme only.
 */
const darkGrey = {
  50: "#FAFCFF",
  100: "#FAFCFF",
  200: "#E4EBF5",
} as const;

export const darkThemeColors = {
  // Primary colors (adjusted for dark theme)
  primary: {
    50: rawColors.brand.primaryDarker,
    100: rawColors.brand.primaryDark,
    200: rawColors.brand.primary,
    // A step below 500, not the same colour: the chart palette's second series reads 300.
    300: rawColors.primary[400],
    400: rawColors.primary[300],
    500: rawColors.brand.primaryLight, // Main brand color in dark theme
    600: rawColors.primary[200],
    700: rawColors.primary[100],
    800: rawColors.primary[50],
    900: rawColors.text.white,
  },

  // Grey scale (inverted)
  grey: {
    50: rawColors.grey[900],
    100: rawColors.grey[800],
    200: rawColors.grey[600],
    300: rawColors.grey[500],
    400: rawColors.grey[400],
    500: darkGrey[200],
    600: darkGrey[100],
    700: darkGrey[50],
    800: rawColors.text.white,
    900: rawColors.text.white,
  },

  // Semantic colors
  background: {
    primary: rawColors.grey[900],
    secondary: rawColors.grey[800],
    tertiary: rawColors.grey[600],
    inverse: rawColors.text.white,
    inverseHover: darkGrey[100],
  },

  surface: {
    primary: rawColors.grey[800],
    secondary: rawColors.grey[600],
    tertiary: rawColors.grey[500],
    detailField: "rgba(170, 151, 219, 0.12)",
    raised: rawColors.grey[600],
    overlay: "rgba(255, 255, 255, 0.1)",
  },

  border: {
    subtle: rawColors.grey[600],
    default: rawColors.grey[500],
    strong: rawColors.grey[400],
    inverse: rawColors.grey[800],
    stroke: rawColors.stroke[300],
  },

  text: {
    primary: rawColors.text.white,
    secondary: darkGrey[100],
    tertiary: darkGrey[200],
    disabled: rawColors.grey[500],
    inverse: rawColors.grey[900],
    link: rawColors.brand.primaryLight,
    linkHover: rawColors.primary[200],
    // Dark-theme counterpart of Figma `text/interactive` — lifted to primary[300]
    // for sufficient contrast on dark surfaces.
    interactive: rawColors.primary[300],
  },

  // Interactive states
  interactive: {
    primary: rawColors.brand.primaryLight,
    primaryHover: rawColors.primary[200],
    primaryActive: rawColors.primary[100],
    primaryDisabled: rawColors.grey[600],

    secondary: rawColors.grey[800],
    secondaryHover: rawColors.grey[600],
    secondaryActive: rawColors.grey[500],

    tertiary: "transparent",
    tertiaryHover: rawColors.grey[800],
    tertiaryActive: rawColors.grey[600],
  },

  // Status colors (adjusted for dark theme)
  success: {
    50: "rgba(27, 190, 101, 0.1)",
    500: rawColors.accent.green,
    600: "#20D96E",
    text: rawColors.accent.green,
  },

  warning: {
    50: "rgba(251, 151, 91, 0.1)",
    500: rawColors.accent.peach,
    600: "#FFA366",
    text: rawColors.accent.peach,
  },

  error: {
    50: "rgba(255, 10, 128, 0.1)",
    500: rawColors.accent.pink,
    600: "#FF3D99",
    text: rawColors.accent.pink,
  },

  info: {
    50: "rgba(28, 174, 200, 0.1)",
    500: rawColors.accent.turquoise,
    600: "#2BC4E0",
    text: rawColors.accent.turquoise,
  },

  // Accent colors
  accent: {
    purple: {
      light: "rgba(93, 27, 190, 0.2)",
      main: rawColors.accent.purple,
    },
    pink: {
      light: "rgba(255, 10, 128, 0.2)",
      main: rawColors.accent.pink,
    },
    peach: {
      light: "rgba(251, 151, 91, 0.2)",
      main: rawColors.accent.peach,
    },
    green: {
      light: "rgba(27, 190, 101, 0.2)",
      main: rawColors.accent.green,
    },
    turquoise: {
      light: "rgba(28, 174, 200, 0.2)",
      main: rawColors.accent.turquoise,
    },
  },

  // AI specific colors
  ai: {
    gradient: rawColors.ai.stroke,
    background: "rgba(170, 151, 219, 0.1)",
    iconPrimary: rawColors.brand.primaryLight,
    iconSecondary: rawColors.primary[200],
  },
} as const;

// Color token types
export type ColorTokens = typeof lightThemeColors;
export type ColorPalette = keyof ColorTokens;
export type ColorShade = keyof ColorTokens[ColorPalette];
